import type React from 'react';

import { PaymentMethodsEnum } from '@ctct/domain-layer';
import { envs, SECURE_PAY_CLIENT_ID, SECURE_PAY_EVENTS, SecurePay } from '@ctct/infrastructure-layer';

import type { SecurePayInfo, SecurePayPageStore } from '../types';

export class SecurePayCommon<TPageStore extends SecurePayPageStore, TPaymentInfo extends SecurePayInfo> {
  private securePayInstance: SecurePay | null = null;

  /**
   * We have a weird fix to inform users if we can't display an iframe with credit card or echeck to them.
   * It works like if we don't receive any messages from an iframe during 2.5 seconds -- we display the message.
   */
  private timeoutIds: number[] = [];

  constructor(
    private pageStore: TPageStore,
    private paymentInfo: TPaymentInfo,
    private partnerName: string,
    private isAmexIrAffiliate: boolean,
    private onTimeoutError?: () => void
  ) {}

  public getCommonData() {
    return {
      getSecurePayIframeId: () => SECURE_PAY_EVENTS.SECUREPAY_ID,

      getSecurePayCreditCardURL: () => {
        const addr = new URL(`https://${envs.getSecurePayEnv()}securepay.ctct.io/payment/cc_buynow.html`);

        addr.searchParams.append('clientId', SECURE_PAY_CLIENT_ID);

        if (this.isAmexDisabled()) {
          addr.searchParams.append('isIrAffiliate', 'true');
        }

        return addr.toString();
      },

      getSecurePayEcheckURL: () => {
        const addr = new URL(`https://${envs.getSecurePayEnv()}securepay.ctct.io/payment/echeck.html`);

        addr.searchParams.append('clientId', SECURE_PAY_CLIENT_ID);

        return addr.toString();
      },
    };
  }

  public getCommonHandlers() {
    return {
      onStartListeningSecurePay: () => {
        this.getSecurePayInstance().startListening();
      },

      onStopListeningSecurePay: () => {
        this.getSecurePayInstance().stopListening();
      },

      clearTimeoutIds: () => {
        this.clearTimeoutIds();
      },

      onSecurePayIframeLoad: (e: React.SyntheticEvent<HTMLIFrameElement, Event>) => {
        const filename = {
          [PaymentMethodsEnum.CREDIT_CARD]: 'ctct/myact-static-buynow.css',
          [PaymentMethodsEnum.ECHECK]: 'ctct/cbs_ftpf_securepay.css',
        }[this.pageStore.getState().selectedMethod];

        if (!filename) return;

        this.getSecurePayInstance().injectCss(e, filename);
        this.addTimeoutId();
      },
    };
  }

  public getSecurePayInstance(): SecurePay {
    if (!this.securePayInstance) {
      this.securePayInstance = new SecurePay(this.securePayCallback.bind(this));
    }

    return this.securePayInstance;
  }

  public getPaymentInfo(): TPaymentInfo {
    return this.paymentInfo;
  }

  public clearTimeoutIds(): void {
    this.timeoutIds.forEach(clearTimeout);
    this.timeoutIds.length = 0;
  }

  /**
   * "1027" => "10/2027"
   * "2025-12-01T05:00:00.000+00:00" => "12/2025" (ISO date string)
   */
  public transformCreditCardExpirationDate(expirationDate?: string): string {
    if (!expirationDate) return '';

    // Handle ISO date strings (e.g., "2025-12-01T05:00:00.000+00:00")
    if (expirationDate.includes('T') || /^\d{4}-\d{2}-\d{2}/.test(expirationDate)) {
      try {
        const date = new Date(expirationDate);

        if (!Number.isNaN(date.getTime())) {
          // Use UTC methods to avoid timezone conversion issues
          const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // getUTCMonth() returns 0-11

          const year = date.getUTCFullYear();

          return `${month}/${year}`;
        }
      } catch {
        // If parsing fails, fall through to other format checks
      }
    }

    if (expirationDate && !expirationDate.includes('/')) {
      return `${expirationDate.slice(0, 2)}/20${expirationDate.slice(2, 4)}`;
    }

    return expirationDate;
  }

  private addTimeoutId(): void {
    this.timeoutIds.push(
      setTimeout(() => {
        const cantReceiveEventsFromIframe = !this.pageStore.getState().addedCssToIframe;

        if (cantReceiveEventsFromIframe) {
          this.pageStore.setState({ showSecurePayIframeBlockedMessage: true });
          this.onTimeoutError?.();
        }
      }, 2500) as unknown as number
    );
  }

  private securePayCallback(eventType: string, data: unknown): void {
    const state = this.pageStore.getState();

    switch (eventType) {
      case SECURE_PAY_EVENTS.GOT_TOKEN_SUCCESS_EVENT: {
        const tokenData = (data as { data?: string | null })?.data ?? null;

        if (state.selectedMethod === PaymentMethodsEnum.CREDIT_CARD) {
          this.pageStore.setState({ creditCardToken: tokenData });
        }

        if (state.selectedMethod === PaymentMethodsEnum.ECHECK) {
          this.pageStore.setState({ echeckToken: tokenData });
        }

        break;
      }

      case SECURE_PAY_EVENTS.CSS_ADDED_EVENT:
        this.pageStore.setState({ addedCssToIframe: true });
        break;

      case SECURE_PAY_EVENTS.INFO_BANK_INFO_MOUSEOVER_EVENT:
      case SECURE_PAY_EVENTS.INFO_SECURECODE_MOUSEOVER_EVENT:
        this.pageStore.setState({ showCvvTooltip: true });
        break;

      case SECURE_PAY_EVENTS.INFO_BANK_INFO_MOUSEOUT_EVENT:
      case SECURE_PAY_EVENTS.INFO_SECURECODE_MOUSEOUT_EVENT:
        this.pageStore.setState({ showCvvTooltip: false });
        break;

      default:
    }
  }

  private isAmexDisabled(): boolean {
    return this.isAmexIrAffiliate && this.partnerName === 'iraffiliate';
  }
}
